import {
  businessCalendarListResponseSchema,
  okResponseSchema,
  slaEscalationSchema,
  slaLookupsSchema,
  slaPolicyListResponseSchema,
  slaPolicySchema,
  type CreateCalendarHolidayRequest,
  type UpdateBusinessCalendarRequest,
  type UpsertSlaEscalationRequest,
  type UpsertSlaPolicyRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api';

export const slaLookupsQueryKey = ['sla', 'lookups'] as const;
export const slaCalendarsQueryKey = ['sla', 'calendars'] as const;
export const slaPoliciesQueryKey = ['sla', 'policies'] as const;

export function useSlaLookups() {
  return useQuery({
    queryKey: slaLookupsQueryKey,
    queryFn: () => apiFetch('/sla/lookups', slaLookupsSchema),
  });
}

export function useSlaCalendars() {
  return useQuery({
    queryKey: slaCalendarsQueryKey,
    queryFn: () => apiFetch('/sla/calendars', businessCalendarListResponseSchema),
  });
}

export function useSlaPolicies() {
  return useQuery({
    queryKey: slaPoliciesQueryKey,
    queryFn: () => apiFetch('/sla/policies', slaPolicyListResponseSchema),
  });
}

export function useUpdateSlaCalendar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateBusinessCalendarRequest }) =>
      apiFetch(`/sla/calendars/${id}`, businessCalendarListResponseSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(slaCalendarsQueryKey, data);
    },
  });
}

export function useAddSlaHoliday() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      calendarId,
      body,
    }: {
      calendarId: string;
      body: CreateCalendarHolidayRequest;
    }) =>
      apiFetch(`/sla/calendars/${calendarId}/holidays`, businessCalendarListResponseSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(slaCalendarsQueryKey, data);
    },
  });
}

export function useRemoveSlaHoliday() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ calendarId, holidayId }: { calendarId: string; holidayId: string }) =>
      apiFetch(
        `/sla/calendars/${calendarId}/holidays/${holidayId}`,
        businessCalendarListResponseSchema,
        { method: 'DELETE' },
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(slaCalendarsQueryKey, data);
    },
  });
}

export function useCreateSlaPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertSlaPolicyRequest) =>
      apiFetch('/sla/policies', slaPolicySchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: slaPoliciesQueryKey });
    },
  });
}

export function useUpdateSlaPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpsertSlaPolicyRequest }) =>
      apiFetch(`/sla/policies/${id}`, slaPolicySchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: slaPoliciesQueryKey });
    },
  });
}

export function useCreateSlaEscalation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ policyId, body }: { policyId: string; body: UpsertSlaEscalationRequest }) =>
      apiFetch(`/sla/policies/${policyId}/escalations`, slaEscalationSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: slaPoliciesQueryKey });
    },
  });
}

export function useUpdateSlaEscalation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpsertSlaEscalationRequest }) =>
      apiFetch(`/sla/escalations/${id}`, slaEscalationSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: slaPoliciesQueryKey });
    },
  });
}

export function useDeleteSlaEscalation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/sla/escalations/${id}`, okResponseSchema, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: slaPoliciesQueryKey });
    },
  });
}
